/**
 * Picks-filter rules shared by both clients. Each race carries its own picks
 * toggle, and the election header carries a master switch that sets every race
 * at once — see `visibleCandidates` for what a race shows and
 * `masterFilterValue` for what the master switch shows.
 */

import { candidateKey } from '@/components/ballot/candidateKey';

export type PicksFilterValue = 'picks' | 'all';

/** The shape `candidateKey` needs, plus the running mate of a joint ticket. */
export interface PickableCandidate {
  id?: string;
  ballotpediaUrl?: string;
  fullName?: string;
  runningMate?: { id?: string; ballotpediaUrl?: string; fullName?: string };
}

type IsLiked = (candidateId: string) => boolean;

/**
 * A joint ticket counts as picked when either member carries a heart, so a
 * partially selected ticket stays visible under the picks filter.
 */
export const isCandidateOrMateLiked = (candidate: PickableCandidate, isLiked: IsLiked): boolean =>
  isLiked(candidateKey(candidate)) ||
  (candidate.runningMate ? isLiked(candidateKey(candidate.runningMate)) : false);

/** Whether any candidate in the list is one of the voter's picks. */
export const hasPicks = (candidates: readonly PickableCandidate[], isLiked: IsLiked): boolean =>
  candidates.some((candidate) => isCandidateOrMateLiked(candidate, isLiked));

/**
 * The candidates a contest shows under the ballot's filter.
 *
 * - `all` — every candidate.
 * - `picks` — every picked candidate when the contest has at least one, so
 *   multiple hearts in one race all stay on screen; the full list when the
 *   contest has none, so filtering keeps races the voter has yet to decide
 *   expanded instead of hiding them.
 */
export const visibleCandidates = <C extends PickableCandidate>(
  candidates: readonly C[],
  filter: PicksFilterValue,
  isLiked: IsLiked
): C[] => {
  if (filter === 'all') return [...candidates];
  const picked = candidates.filter((candidate) => isCandidateOrMateLiked(candidate, isLiked));
  return picked.length > 0 ? picked : [...candidates];
};

/**
 * A race's effective filter: the value its own toggle set, else the election's
 * master switch.
 */
export const contestFilterValue = (
  contestFilter: PicksFilterValue | undefined,
  electionFilter: PicksFilterValue
): PicksFilterValue => contestFilter ?? electionFilter;

/**
 * What the election header's master switch shows. A race's own toggle can move
 * it off the master's value, so the switch reads `picks` only while every race
 * with a toggle shows picks — then one tap always sets every race to the value
 * the switch moves to. With no such race, it shows the stored value.
 */
export const masterFilterValue = (
  electionFilter: PicksFilterValue,
  contestFilters: readonly PicksFilterValue[]
): PicksFilterValue => {
  if (contestFilters.length === 0) return electionFilter;
  return contestFilters.every((filter) => filter === 'picks') ? 'picks' : 'all';
};
